import { QueryKey, useQuery, UseQueryOptions, UseQueryResult, skipToken } from '@tanstack/react-query';
import { useLanguage } from '../../../core/i18n/hooks/use-language';

import { makeAnonymousFetcher } from '../../../core/requests/anonymous-fetcher';
import { retryRequestExceptForClientErrors } from '../services/retry-request-except-for-client-errors';
import { QueryParameters } from './use-authenticated-query';

export type QueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = {
  options: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'>;
  url: string;
  headers?: Record<string, string>;
};

export function useAnonymousQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(params: QueryParameters<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
  const currentLanguage = useLanguage();

  return useQuery<TQueryFnData, TError, TData, TQueryKey>(
    params === skipToken
      ? {
          queryFn: skipToken,
          // this typecast is applied because it strictly does not adhere to the TQueryKey type but it is the desired key for this scenario
          queryKey: ['skipToken'] as unknown as TQueryKey,
        }
      : {
          ...params.options,
          retry: params.options.retry ?? retryRequestExceptForClientErrors,
          queryFn: makeAnonymousFetcher<TQueryFnData>({
            url: params.url,
            language: currentLanguage,
            headers: params.headers,
          }),
        },
  );
}
